import { format, parseISO, startOfDay, subDays } from "date-fns";
import { ITimesheetData } from "./types";

const formatReadableTimeSlot = (date: string, start: string, end: string) => {
  const dateObj = parseISO(date); // "2025-05-16"
  const startTime = parseISO(`${date}T${start}`); // "08:00:00"
  const endTime = parseISO(`${date}T${end}`); // "20:00:00"

  const formattedDate = format(dateObj, "EEE d MMM yyyy"); // e.g. "Fri 16 May 2025"
  const formattedStart = format(startTime, "h:mmaaa"); // e.g. "8:00AM"
  const formattedEnd = format(endTime, "h:mmaaa"); // e.g. "8:00PM"

  return `${formattedDate}, ${formattedStart.toLowerCase()} to ${formattedEnd.toLowerCase()}`;
};

const formatApprovedOn = (datetime: string | null): string | null => {
  if (!datetime) return null;
  try {
    const utcDate = parseISO(datetime);
    // Convert UTC to AEST/AEDT (Australia/Sydney timezone) using native JS
    const aestDate = new Date(utcDate.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
    return format(aestDate, "d MMM yyyy, h:mmaaa"); // "7 Feb 2025, 3:24pm"
  } catch {
    return null;
  }
};

export function parseTimesheetData(item: any): ITimesheetData | null {
  item.rating = item?.rating || item?.candidate?.rating || null;
  const safeRating = item.rating
    ? {
        average_rating:
          item.rating.average_rating != null
            ? Math.round(item.rating.average_rating)
            : null,
        latest_rating:
          item.rating.latest_rating != null
            ? Math.round(item.rating.latest_rating)
            : null,
        ratings: Array.isArray(item.rating.ratings)
          ? item.rating.ratings.map((r: any) => r.rating ?? 0)
          : [],
      }
    : {
        average_rating: null,
        latest_rating: null,
        ratings: [],
      };

  const safeVacancy = item?.vacancy ?? {
    id: 0,
    uuid: "",
    matching_position_id: 0,
    posted: "",
    matching_position: {
      id: 0,
      name: "",
      uuid: "",
      max_base_rate: "",
      max_charge_rate: "",
      min_base_rate: "",
      min_charge_rate: ""
    },
  };

  // Handle decline data from different API structures
  const declineSource = item?.declined_timesheet || item?.decline_reason;
  const safeDeclineReason = declineSource ? {
    id: declineSource.id ?? "",
    uuid: declineSource.uuid ?? "",
    timesheet_id: declineSource.timesheet_id ?? "",
    message: declineSource.reason ?? declineSource.message ?? "",
    created_on: declineSource.created_on ?? "",
    created_at: declineSource.created_at ?? "",
    updated_at: declineSource.updated_at ?? "",
  } : {
    id: "",
    uuid: "",
    timesheet_id: "",
    message: "",
    created_on: "",
    created_at: "",
    updated_at: "",
  };

  if (item?.timeframe_id) {
    const start = item?.start_time || "";
    const end = item?.end_time || "";
    const rawDate = item?.date ? new Date(item.date) : null;
    const date = rawDate?.toISOString().split("T")[0] || "";
 
    return {
      id: String(item.timeframe_id),
      candidate: {
        id: item.user_id || 0,
        uuid: item.user_uuid || "",
        first_name: item.first_name || "",
        last_name: item.last_name || "",
        photo: item.user_photo || null,
        profile: {
          candidate_id: item?.candidate?.profile?.candidate_id || 0,
          loading_amount: item?.candidate?.profile?.loading_amount || 0,
        },
        is_documents_expired: item.candidate?.is_documents_expired || false,
      },
      centre: {
        id: item.centre_id,
        business_name: item.business_name || "",
        address: item.centre_address || "",
        photo: item.centre_photo || null,
        uuid: "", // Not available in this format
      },
      timeframe: formatReadableTimeSlot(date, start, end),
      status_id: item.status_id ?? null,
      status: item.status || "",
      rating: safeRating,
      submitted_on: item.submitted_on ?? null,
      approved_on: formatApprovedOn(item.approved_on),
      break_start_time: item.break_start_time ?? null,
      break_end_time: item.break_end_time ?? null,
      vacancy_id: item.vacancy_id ?? "",
      timeframe_id: item.timeframe_id ?? null,
      vacancy: safeVacancy,
      position_name: item.position_name ?? "",
      job_start_time: item.job_start_time
        ? format(parseISO(`${date}T${start}`), "h:mmaaa")
        : "",
      job_end_time: item.job_end_time
        ? format(parseISO(`${date}T${end}`), "h:mmaaa")
        : "",
      decline_reason: safeDeclineReason,
      approver: item.approver ?? null,
      approved_by: item.approved_by ?? null,
      approver_ip: item.approver_ip ?? null,
      allowance: item.allowance ?? null,
      max_base_rate: item.max_base_rate ?? null,
      max_charge_rate: item.max_charge_rate ?? null,
      min_base_rate: item.min_base_rate ?? null,
      min_charge_rate: item.min_charge_rate ?? null,
      sent_to_payroll_on: item?.sent_to_payroll_on ?? null,
      loading_base_rate: item?.loading_base_rate ?? null,
      loading_charge_rate: item?.loading_charge_rate ?? null,
      allowance_base_rate: item?.allowance_base_rate ?? null,
      allowance_charge_rate: item?.allowance_charge_rate ?? null,
      timesheet_status: item?.timesheet_status || null,
      is_declined_timesheet: item?.is_declined_timesheet ?? false,
      declined_info: item?.declined_info ?? null,
    };
  }

  if ((item?.job_start_time && item?.job_end_time) || item?.did_not_work) {
    const job_start = new Date(item.job_start_time).toISOString().slice(11, 16);
    const job_end = new Date(item.job_end_time).toISOString().slice(11, 16);
    const start = item?.timeframe?.start_time || "";
    const end = item?.timeframe?.end_time || "";
    const rawDate = new Date(item.job_start_time || item?.timeframe?.start_date);
    const date = rawDate.toISOString().split("T")[0];

    return {
      id: String(item.id),
      candidate: {
        id: item.candidate?.id || 0,
        uuid: item.candidate?.uuid || "",
        first_name: item.candidate?.first_name || "",
        last_name: item.candidate?.last_name || "",
        photo: item.candidate?.photo || null,
        profile: {
          candidate_id: item?.candidate?.profile?.candidate_id || 0,
          loading_amount: item?.candidate?.profile?.loading_amount || 0,
        },
        is_documents_expired: item.candidate?.is_documents_expired || false,
      },
      centre: {
        id: item.centre?.id || item.centre_id || 0,
        business_name: item.centre?.business_name || "",
        address: item.centre?.address || "",
        photo: item.centre?.photo || null,
        uuid: item.centre?.uuid || "",
      },
      timeframe: item?.timesheet_date_time,
      status_id: item.status_id ?? null,
      status: item.status?.name || "", // status is now an object in this variant
      rating: safeRating,
      submitted_on: item.submitted_on ?? null,
      approved_on: formatApprovedOn(item.approved_on),
      break_start_time: item.break_start_time ?? null,
      break_end_time: item.break_end_time ?? null,
      vacancy_id: item.vacancy_id ?? "",
      timeframe_id: item.vacancy_timeframe_id ?? null,
      vacancy: safeVacancy,
      position_name: item.position_name ?? "",
      job_start_time: item.job_start_time
        ? format(parseISO(`${date}T${job_start}`), "h:mmaaa")
        : "",
      job_end_time: item.job_end_time
        ? format(parseISO(`${date}T${job_end}`), "h:mmaaa")
        : "",
      decline_reason: safeDeclineReason,
      approver: item.approver ?? null,
      approved_by: item.approved_by ?? null,
      approver_ip: item.approver_ip ?? null,
      allowance: item.allowance ?? null,
      max_base_rate: item.max_base_rate ?? null,
      max_charge_rate: item.max_charge_rate ?? null,
      min_base_rate: item.min_base_rate ?? null,
      min_charge_rate: item.min_charge_rate ?? null,
      sent_to_payroll_on: item?.sent_to_payroll_on ?? null,
      loading_base_rate: item?.loading_base_rate ?? null,
      loading_charge_rate: item?.loading_charge_rate ?? null,
      allowance_base_rate: item?.allowance_base_rate ?? null,
      allowance_charge_rate: item?.allowance_charge_rate ?? null,
      payroll_approved_on: item?.payroll_approved_on ?? null,
      payroll_approved_by: item?.payroll_approved_by ?? null,
      did_not_work: item?.did_not_work ?? false,
      timesheet_status: item?.timesheet_status || null,
      is_declined_timesheet: item?.is_declined_timesheet ?? false,
      declined_info: item?.declined_info ?? null,
    };
  }

  console.warn("Unrecognized timesheet data format", item);
  return null;
}

export const isSameDateRange = (
  range1: [Date | null, Date | null] | null,
  range2: [Date | null, Date | null] | null
): boolean => {
  if (!range1 && !range2) return true;
  if (!range1 || !range2) return false;

  const [start1, end1] = range1;
  const [start2, end2] = range2;

  const sameStart =
    (!start1 && !start2) ||
    (start1 && start2 && start1.getTime() === start2.getTime());
  const sameEnd =
    (!end1 && !end2) || (end1 && end2 && end1.getTime() === end2.getTime());

  return Boolean(sameStart && sameEnd);
};

type DateRange = [Date | null, Date | null] | null;

interface DateParams {
  start_date?: string;
  end_date?: string;
}

export function getDateParamsForAPI(dateRange: DateRange): DateParams {
  if (!dateRange || (!dateRange[0] && !dateRange[1])) return {};

  const start = dateRange[0] ?? dateRange[1]; // fallback if start is null
  const end = dateRange[1] ?? dateRange[0]; // fallback if end is null

  if (!start || !end) return {};

  const formattedStart = format(start, "yyyy-MM-dd");
  const formattedEnd = format(end, "yyyy-MM-dd");

  return {
    start_date: formattedStart,
    end_date: formattedEnd,
  };
}

/**
 * Returns a date range from (today - days + 1) to today.
 * @param days Number of days to go back (e.g., 7 or 30)
 * @returns [startDate, endDate]
 */
export function getDefaultDateRange(days: number): [Date, Date] {
  const today = startOfDay(new Date());
  const startDate = subDays(today, days - 1); // includes today as last day
  return [startDate, today];
}
